import  { useEffect } from "react";
import { Form, Select, Flex, Radio, Button } from "antd";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { validateConfig } from "../../../config/validate.config";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";

const { Option, OptGroup } = Select;

const CLINICAL_PREFIX = "clinical:";
const isClinicalValue = (v) =>
  typeof v === "string" && v.startsWith(CLINICAL_PREFIX);

const rolesWithIsHodQuestion = [
  "Doctor",
  "Nurse",
  "Physiotherapist",
  "Speech_Swallow_Therapist",
  "Respiratory_Therapist",
  "Nutritionist",
  "Psychologist",
];
const rolesWithoutHodField = [
  "Organization_Admin",
  "Organization_Owner",
  "Facility_Admin",
  "Receptionist",
];

const rolesWithIsAdminQuestion = ["Pharmacist"];

const rolesWithFacility = [
  "Doctor",
  "Nurse",
  "Physiotherapist",
  "Speech_Swallow_Therapist",
  "Respiratory_Therapist",
  "Nutritionist",
  "Receptionist",
  "Facility_Admin",
  "Psychologist",
  // UsersForm already treats Pharmacist as facility-scoped when submitting;
  // without it here the field never rendered, so the role was saved with no
  // facility and pharmacy screens then sent facility=null.
  "Pharmacist",
];

const RoleForm = ({
  userRoles,
  clinicalRoles = [],
  editListData,
  selectedRoles,
  setSelectedRoles,
  form,
  facilities,
  params,
  userDetails,
}) => {
  useEffect(() => {
    if (editListData?.roles) {
      const initialSelectedRoles = editListData?.roles?.map(
        (role) => role?.role
      );
      setSelectedRoles(initialSelectedRoles);
    }
  }, [editListData]);

  const defaultRole = userDetails?.currRole || getCookie(Role);

  const filteredWithoutHodRoles =
    defaultRole === "Facility_Admin"
      ? userRoles?.filter(
          (role) =>
            role?.value !== "Organization_Owner" &&
            role?.value !== "Organization_Admin"
        )
      : defaultRole === "Organization_Admin"
      ? userRoles?.filter((role) => role?.value !== "Organization_Owner")
      : userRoles;

  const handleRoleChange = (index, value) => {
    const updatedRoles = [...selectedRoles];
    updatedRoles[index] = value;
    setSelectedRoles(updatedRoles);

    if (editListData) {
      const newValues = [...form.getFieldsValue().roles];
      newValues[index].is_hod = null;
      form.setFieldsValue({ roles: newValues });
    }
  };

  const handleRemove = (name, index) => {
    const updatedRoles = [...selectedRoles];
    updatedRoles.splice(index, 1);
    setSelectedRoles(updatedRoles);

    form.setFieldsValue({
      roles: form.getFieldsValue().roles.filter((_, idx) => idx !== name),
    });
  };

  const roleRows = Form.useWatch("roles", form) || [];

  const facilityOf = (row) => row?.facility ?? null;

  const otherRows = (index) =>
    roleRows.filter((row, i) => i !== index && row?.role);

  /* Which facilities a given role could still be added at, ignoring this row. */
  const facilitiesFreeFor = (role, index) => {
    const usedFacilityIds = otherRows(index)
      .filter((row) => row.role === role)
      .map((row) => row.facility)
      .filter((id) => id !== undefined && id !== null);
    return (facilities || []).filter(
      (facility) => !usedFacilityIds.includes(facility?.id)
    );
  };

  /*
   * Whether this row can still take a role at all.
   *
   * A new row has no facility yet, so comparing it against the rows that do
   * found nothing in common and offered every role back - including one already
   * assigned at the only facility on the account. Picking it then left the
   * facility list empty and the row unfillable. A facility-scoped role is only
   * offered while some facility is still free for it, which on a single-facility
   * account means exactly once.
   */
  const roleStillAssignable = (role, index) => {
    /* shouldDisplayFacilityField is declared below; both are only ever called
       while rendering, by which point it is set. */
    if (!shouldDisplayFacilityField(role)) return true;
    if (facilityOf(roleRows[index]) != null) return true;
    return facilitiesFreeFor(role, index).length > 0;
  };

  const getAvailableRoles = (index) => {
    const thisFacility = facilityOf(roleRows[index]);
    const takenAtThisFacility = otherRows(index)
      .filter((row) => facilityOf(row) === thisFacility)
      .map((row) => row.role);

    return filteredWithoutHodRoles.filter(
      (role) =>
        !takenAtThisFacility.includes(role.value) &&
        roleStillAssignable(role.value, index)
    );
  };

  const getAvailableClinicalRoles = (index) => {
    const thisFacility = facilityOf(roleRows[index]);
    const takenAtThisFacility = otherRows(index)
      .filter((row) => isClinicalValue(row.role))
      .filter((row) => facilityOf(row) === thisFacility)
      .map((row) => row.role);
    return clinicalRoles.filter((r) => {
      const value = `${CLINICAL_PREFIX}${r.id}`;
      return (
        !takenAtThisFacility.includes(value) && roleStillAssignable(value, index)
      );
    });
  };

  const getAvailableFacilities = (index) => {
    const thisRole = roleRows[index]?.role;
    if (!thisRole) return facilities || [];
    return facilitiesFreeFor(thisRole, index);
  };

  // roles for which is_hod option to be visible. Clinical roles are
  // effectively Clinical_Practitioner, so they get the hod question too.
  const shouldDisplayHodField = (role) => {
    if (isClinicalValue(role)) return true;
    return (
      rolesWithIsHodQuestion.includes(role) &&
      !rolesWithoutHodField.includes(role)
    );
  };
  // roles for which facility option to be visible
  const shouldDisplayFacilityField = (role) => {
    if (isClinicalValue(role)) return true;
    return rolesWithFacility.includes(role);
  };

  return (
    <Form.List name="roles">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }, index) => {
            const showFacility =
              shouldDisplayFacilityField(selectedRoles[index]) &&
              defaultRole !== "Facility_Admin";
            const showHod = shouldDisplayHodField(selectedRoles[index]);
            const showIsAdmin = rolesWithIsAdminQuestion.includes(
              selectedRoles[index]
            );
            return (
              <div key={key} style={{ marginBottom: 16 }}>
                <Flex gap={16} align="start" wrap>
                  <Form.Item
                    label="Role"
                    name={[name, "role"]}
                    rules={validateConfig.isRequired("Role")}
                    style={{
                      flex: "0 0 300px",
                      maxWidth: 300,
                      marginBottom: 0,
                    }}
                  >
                    <Select
                      placeholder="Choose role"
                      className={inputStyles.mdSelect}
                      optionFilterProp="label"
                      showSearch
                      onChange={(value) => handleRoleChange(index, value)}
                    >
                      <OptGroup label="Roles">
                        {getAvailableRoles(index).map((role) => (
                          <Option
                            value={role.value}
                            key={role.value}
                            label={role.label}
                          >
                            {role.label}
                          </Option>
                        ))}
                      </OptGroup>
                      {getAvailableClinicalRoles(index).length > 0 && (
                        <OptGroup label="Clinical Roles">
                          {getAvailableClinicalRoles(index).map((r) => (
                            <Option
                              value={`${CLINICAL_PREFIX}${r.id}`}
                              key={`${CLINICAL_PREFIX}${r.id}`}
                              label={r.name}
                            >
                              {r.name}
                            </Option>
                          ))}
                        </OptGroup>
                      )}
                    </Select>
                  </Form.Item>

                  {showFacility && (
                    <Form.Item
                      label="Facility"
                      name={[name, "facility"]}
                      rules={validateConfig.isRequired("Facility")}
                      style={{
                        flex: "0 0 300px",
                        maxWidth: 300,
                        marginBottom: 0,
                      }}
                    >
                      <Select
                        placeholder="Choose facility"
                        className={inputStyles.mdSelect}
                        optionFilterProp="children"
                      >
                        {getAvailableFacilities(index).map((facility) => (
                          <Option value={facility?.id} key={facility?.id}>
                            {facility?.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}

                  <DeleteOutlined
                    onClick={() => handleRemove(name, index)}
                    style={{
                      alignSelf: "center",
                      marginTop: 24,
                      color: "#667085",
                      cursor: "pointer",
                    }}
                  />
                </Flex>

                {showIsAdmin && (
                  <Form.Item
                    label="Is admin?"
                    {...restField}
                    name={[name, "is_admin"]}
                    initialValue={false}
                    rules={[{ required: true, message: "Please choose" }]}
                    style={{ marginTop: 12, marginBottom: 0 }}
                    tooltip="A Pharmacy Admin can add other pharmacist users. No means ordinary pharmacy staff."
                  >
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}

                {showHod && (
                  <Form.Item
                    label="Is hod?"
                    {...restField}
                    name={[name, "is_hod"]}
                    rules={[{ required: true, message: "Please choose" }]}
                    style={{ marginTop: 12, marginBottom: 0 }}
                  >
                    <Radio.Group>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                )}
              </div>
            );
          })}
          <Form.Item>
            <Button
              type="dashed"
              style={{ width: "50%" }}
              onClick={() => {
                add();
                setSelectedRoles([...selectedRoles, null]); // Add a placeholder for the new role
              }}
              block
              icon={<PlusOutlined />}
            >
              Add role
            </Button>
          </Form.Item>
        </>
      )}
    </Form.List>
  );
};

export default RoleForm;
